import { TABLE_EVENT } from 'constants/table';
import React, { useCallback } from 'react';
import { useObserver } from 'hooks/useObserver';
import { usePlayerContext } from 'contexts/PlayerContext/PlayerContext';
import {
    TableEventSubscriptionTypes,
    TableEventPayload,
} from 'helpers/tableEvent/tableEvent';
import { useAutoPlay } from 'components/SamplesTable/hooks/useAutoPlay';

export const SamplesTableRowObserver: React.FC<{
    eventSubscription: TableEventSubscriptionTypes;
    rowIndex?: number;
    rowRef: React.RefObject<HTMLTableRowElement>;
    setIsRowFocused: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ eventSubscription, rowIndex, rowRef, setIsRowFocused }) => {
    const { playFromBeginning, togglePlaying } = usePlayerContext();

    const handlePlay = useCallback(
        (type: 'toggle' | 'start') => {
            (document.activeElement as HTMLElement).blur();
            rowRef.current?.focus();

            type === 'start' && playFromBeginning();
            type === 'toggle' && togglePlaying();
        },
        [togglePlaying, playFromBeginning, rowRef],
    );

    useAutoPlay({ rowIndex, handlePlay });

    const observerOnArrowNavigation = useCallback(
        (payload: TableEventPayload) => {
            const isFocused = payload.rowIndex === rowIndex;

            if (isFocused) {
                handlePlay('start');
            }
            setIsRowFocused(isFocused);
        },
        [rowIndex, handlePlay, setIsRowFocused],
    );

    const observerOnClick = useCallback(
        (payload: TableEventPayload) => {
            const isCurrentRowTargetted = payload.rowIndex === rowIndex;

            if (isCurrentRowTargetted) {
                handlePlay('toggle');
            }
            setIsRowFocused(isCurrentRowTargetted);
        },
        [rowIndex, handlePlay, setIsRowFocused],
    );

    useObserver<TABLE_EVENT, TableEventPayload>(
        TABLE_EVENT.ROW_CHANGED,
        observerOnArrowNavigation,
        eventSubscription.subscribe,
        eventSubscription.unsubscribe,
    );

    useObserver<TABLE_EVENT, TableEventPayload>(
        TABLE_EVENT.ROW_CLICKED,
        observerOnClick,
        eventSubscription.subscribe,
        eventSubscription.unsubscribe,
    );

    return null;
};
